'use client';

import React, { Fragment, ReactElement, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { HiOutlineSparkles } from 'react-icons/hi2';
import WidgetComponent from '@/components/widgets/WidgetComponent';
import CallBotComponent from '@/components/widgets/CallBotComponent';

interface DemoModalProps {
  botId: string;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  infoText: string;
  loadingTest: string;
  demoComponent: 'ModalAutoPlayAllOptionActive' | 'ModalWaitingPressPlay' | 'ModalForCallBot';
  icon: ReactElement;
}

const DemoModal: React.FC<DemoModalProps> = ({
  botId,
  isOpen,
  onClose,
  title,
  infoText,
  loadingTest,
  demoComponent,
  icon,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLoadingTimeout(true);
    }, 100);

    // Reset states when modal opens
    if (isOpen) {
      setIsLoading(true);
      setLoadingTimeout(false);
    }

    // Cleanup
    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  const handleWidgetLoad = () => {
    setIsLoading(false);
  };

  let widgetToLoad: React.ReactNode;
  switch (demoComponent) {
    case 'ModalAutoPlayAllOptionActive':
      widgetToLoad = (
        <WidgetComponent
          botId={botId}
          onLoad={handleWidgetLoad}
          mute={true}
          hideCloseButton={true}
          hideMinimizeButton={true}
          open={true}
          loadingText={loadingTest}
        />
      );
      break;
    case 'ModalWaitingPressPlay':
      widgetToLoad = (
        <WidgetComponent
          botId={botId}
          onLoad={handleWidgetLoad}
          mute={false}
          hideCloseButton={true}
          hideMinimizeButton={true}
          open={true}
          loadingText={loadingTest}
        />
      );
      break;
    case 'ModalForCallBot':
      widgetToLoad = (
        <CallBotComponent
          botId={botId}
          onLoad={handleWidgetLoad}
          mute={false}
          hideCloseButton={true}
          hideMinimizeButton={true}
          open={false}
          loadingText={loadingTest}
        />
      );
      break;
    default:
      break;
  }

  return (
    <>
      {/* Main Modal */}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="tw-relative tw-z-50" onClose={onClose}>
          {/* Background overlay */}
          <Transition.Child
            as={Fragment}
            enter="tw-ease-out tw-duration-300"
            enterFrom="tw-opacity-0"
            enterTo="tw-opacity-100"
            leave="tw-ease-in tw-duration-200"
            leaveFrom="tw-opacity-100"
            leaveTo="tw-opacity-0"
          >
            <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-backdrop-blur-sm" />
          </Transition.Child>

          {/* Modal content */}
          <div className="tw-fixed tw-inset-0 tw-overflow-hidden">
            <div className="tw-flex tw-min-h-full tw-items-center tw-justify-center tw-p-1 sm:tw-p-4 tw-text-center">
              <Transition.Child
                as={Fragment}
                enter="tw-ease-out tw-duration-300"
                enterFrom="tw-opacity-0 tw-scale-95"
                enterTo="tw-opacity-100 tw-scale-100"
                leave="tw-ease-in tw-duration-200"
                leaveFrom="tw-opacity-100 tw-scale-100"
                leaveTo="tw-opacity-0 tw-scale-95"
              >
                <Dialog.Panel className="tw-w-full tw-max-w-[98vw] sm:tw-max-w-md md:tw-max-w-lg lg:tw-max-w-xl tw-max-h-[98vh] sm:tw-max-h-[95vh] tw-transform tw-overflow-hidden tw-rounded sm:tw-rounded-2xl tw-bg-white tw-text-left tw-align-middle tw-shadow-xl tw-transition-all tw-relative tw-flex tw-flex-col">
                  {/* Modal Header */}
                  <div className="tw-relative tw-flex tw-items-center tw-justify-center tw-px-2 tw-py-2 tw-flex-shrink-0 tw-border-b tw-border-gray-100">
                    <div className="tw-hidden lg:tw-block tw-absolute tw-left-2 sm:tw-left-3">{icon}</div>

                    <Dialog.Title
                      as="h3"
                      className="tw-text-base sm:tw-text-base lg:tw-text-lg tw-font-normal tw-text-gray-900 tw-leading-tight tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-8"
                    >
                      <span className="tw-truncate tw-text-center">{title}</span>
                      <IoInformationCircleOutline
                        size={24}
                        className="tw-text-blue-600 tw-cursor-pointer tw-flex-shrink-0"
                        onClick={() => setIsInfoPanelOpen(true)}
                      />
                    </Dialog.Title>

                    <button
                      onClick={onClose}
                      className="tw-absolute tw-right-2 sm:tw-right-3 tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors tw-p-1"
                    >
                      <span className="tw-text-xl">x</span>
                    </button>
                  </div>

                  {/* Loading Indicator */}
                  {isLoading && !loadingTimeout && (
                    <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/90 tw-z-10">
                      <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 sm:tw-gap-4">
                        <div className="tw-animate-spin">
                          <HiOutlineSparkles className="tw-w-5 tw-h-5 sm:tw-w-8 sm:tw-h-8 tw-text-blue-600" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Widget Container */}
                  <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0">
                    <div className="tw-h-full tw-w-full">{widgetToLoad}</div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Information Panel Overlay */}
      <Transition appear show={isInfoPanelOpen} as={Fragment}>
        <Dialog as="div" className="tw-relative tw-z-[60]" onClose={() => setIsInfoPanelOpen(false)}>
          {/* Background overlay */}
          <Transition.Child
            as={Fragment}
            enter="tw-ease-out tw-duration-300"
            enterFrom="tw-opacity-0"
            enterTo="tw-opacity-100"
            leave="tw-ease-in tw-duration-200"
            leaveFrom="tw-opacity-100"
            leaveTo="tw-opacity-0"
          >
            <div className="tw-fixed tw-inset-0 tw-bg-black/60 tw-backdrop-blur-sm" />
          </Transition.Child>

          {/* Panel content */}
          <div className="tw-fixed tw-inset-0 tw-overflow-hidden">
            <div className="tw-flex tw-min-h-full tw-items-end sm:tw-items-center tw-justify-center tw-p-2 sm:tw-p-4">
              <Transition.Child
                as={Fragment}
                enter="tw-ease-out tw-duration-300"
                enterFrom="tw-opacity-0 tw-translate-y-4 sm:tw-translate-y-0 sm:tw-scale-95"
                enterTo="tw-opacity-100 tw-translate-y-0 sm:tw-scale-100"
                leave="tw-ease-in tw-duration-200"
                leaveFrom="tw-opacity-100 tw-translate-y-0 sm:tw-scale-100"
                leaveTo="tw-opacity-0 tw-translate-y-4 sm:tw-translate-y-0 sm:tw-scale-95"
              >
                <Dialog.Panel className="tw-w-full tw-max-w-lg tw-max-h-[80vh] tw-transform tw-overflow-hidden tw-rounded-t-2xl sm:tw-rounded-2xl tw-bg-white tw-text-left tw-align-middle tw-shadow-xl tw-transition-all tw-flex tw-flex-col">
                  {/* Panel Header */}
                  <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-gray-100 tw-flex-shrink-0">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <IoInformationCircleOutline className="tw-w-5 tw-h-5 tw-text-blue-600" />
                      <Dialog.Title as="h3" className="tw-text-lg tw-font-semibold tw-text-gray-900">
                        Informations
                      </Dialog.Title>
                    </div>
                    <button
                      onClick={() => setIsInfoPanelOpen(false)}
                      className="tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors tw-p-1"
                    >
                      <span className="tw-text-xl">x</span>
                    </button>
                  </div>

                  {/* Panel Content */}
                  <div className="tw-flex-1 tw-overflow-y-auto tw-p-4">
                    <div className="tw-bg-blue-50 tw-p-4 tw-rounded-lg">
                      <p className="tw-text-sm sm:tw-text-base tw-text-blue-800 tw-leading-relaxed tw-whitespace-pre-wrap">
                        {infoText}
                      </p>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default DemoModal;
